import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import { UPDATE_WALLET_RULE } from '@/store/modules/wallet.module'
import { FLAG_CONSTANTS } from '@/constants'
import { cloneDeep } from 'lodash'
import { loadOptionsCollectionsWithPriceBooks } from '@/views/apps/wallet/wallet-rules.utils'

const {
  calculateCommercialTerms,
  convertPercentToFraction,
  convertValueToPercent,
  round,
} = utils

const {
  PAYMENT_TERMS,
  LOGISTICS_TERMS,
  CURRENCY_CONVERSIONS,
} = c

const availableCurrencies = Object.keys(CURRENCY_CONVERSIONS)

export { PAYMENT_TERMS, LOGISTICS_TERMS, availableCurrencies }

export function useEditUser() {
  const localUserData = ref(null)
  const discount = ref('')
  const isEditing = ref(false)
  const exampleWholeSalePrice = ref(null)
  const selectedPriceBooks = ref([])
  const selectedSegmentationBooks = ref([])
  const collectionWithPriceBooks = ref([{ priceBooks: [] }])
  const collectionWithPriceBooksRawData = ref([])
  const collectionWithSegmentationBooks = ref([{ priceBooks: [] }])
  const collectionWithSegmentationBooksRawData = ref([])
  const userDataTransactionCurrency = ref(null)

  const priceBooks = computed(() => store.state.collection?.priceBooks || [])

  const hasTransactionCurrencyFeature = computed(() => store.state.flags[FLAG_CONSTANTS.TRANSACTION_CURRENCY])

  const getUpdatedPriceForRetailer = computed(() => {
    let convertedDiscount = 0
    if (discount.value > 100) {
      convertedDiscount = convertPercentToFraction(discount.value)
    } else {
      convertedDiscount = calculateCommercialTerms(discount.value)
    }
    return round(exampleWholeSalePrice.value * convertedDiscount)
  })

  const selectedPriceBookIds = computed(() => {
    const pbIds = (selectedPriceBooks.value || []).map(book => book._id) || []
    const sbIds = (selectedSegmentationBooks.value || []).map(book => book._id) || []
    return [...pbIds, ...sbIds]
  })

  const regularPriceBooks = computed(() => {
    if (hasTransactionCurrencyFeature.value) {
      return priceBooks.value.filter(
        book => book.type === 'pricebook' && localUserData.value?.transactionCurrency === book.currency,
      )
    }
    return priceBooks.value
  })

  const segmentationBooksData = computed(() => priceBooks.value.filter(book => book.type === 'segmentation'))

  const isPriceBooksEmpty = computed(() => collectionWithPriceBooks.value.every(collection => !collection.priceBooks.length))

  const isSegmentationBooksEmpty = computed(() => collectionWithSegmentationBooks.value.every(collection => !collection.priceBooks.length))

  const discountError = computed(() => {
    if (discount.value === '' || discount.value === null) return 'Discount is required'
    if (discount.value < 0 || discount.value > 100) return 'Discount must be between 0 and 100'
    return ''
  })

  const paymentTermOptions = computed(() => Object.values(PAYMENT_TERMS).map(t => t.TEXT))

  const logisticsTermOptions = computed(() => Object.values(LOGISTICS_TERMS))

  function initEditUser(userDetails) {
    localUserData.value = cloneDeep(userDetails)
    setPriceBooks()
    updateDiscount()
    userDataTransactionCurrency.value = localUserData.value.transactionCurrency
  }

  function loadSelectedPriceBooks() {
    selectedPriceBooks.value = regularPriceBooks.value.filter(
      book => (localUserData.value?.priceBookIds || []).includes(book._id),
    )
  }

  function loadSelectedSegmentationBooks() {
    selectedSegmentationBooks.value = segmentationBooksData.value.filter(
      book => (localUserData.value?.priceBookIds || []).includes(book._id),
    )
  }

  function setPriceBooks() {
    const regularBooks = regularPriceBooks.value || []
    const segBooks = segmentationBooksData.value || []
    collectionWithPriceBooks.value = loadOptionsCollectionsWithPriceBooks(regularBooks) || [{ priceBooks: [] }]
    collectionWithPriceBooksRawData.value = collectionWithPriceBooks.value
    loadSelectedPriceBooks()
    collectionWithSegmentationBooks.value = loadOptionsCollectionsWithPriceBooks(segBooks) || [{ priceBooks: [] }]
    collectionWithSegmentationBooksRawData.value = collectionWithSegmentationBooks.value
    loadSelectedSegmentationBooks()
  }

  function onSearchPriceBooks(search = '') {
    if (search) {
      collectionWithPriceBooks.value = collectionWithPriceBooksRawData.value.map(collection => ({
        ...collection,
        priceBooks: collection.priceBooks.filter(
          book => book.priceBookName.toLowerCase().includes(search.toLowerCase()),
        ),
      }))
      return
    }
    collectionWithPriceBooks.value = loadOptionsCollectionsWithPriceBooks(regularPriceBooks.value)
  }

  function onSearchSegmentationBooks(search = '') {
    if (search) {
      collectionWithSegmentationBooks.value = collectionWithSegmentationBooksRawData.value.map(collection => ({
        ...collection,
        priceBooks: collection.priceBooks.filter(
          book => book.priceBookName.toLowerCase().includes(search.toLowerCase()),
        ),
      }))
      return
    }
    collectionWithSegmentationBooks.value = loadOptionsCollectionsWithPriceBooks(segmentationBooksData.value)
  }

  function handleSelectPriceBook(priceBook) {
    if (!priceBook?._id) return
    if (selectedPriceBookIds.value.includes(priceBook._id)) {
      deselectPriceBook(priceBook)
      return
    }
    selectedPriceBooks.value.push(priceBook)
  }

  function deselectPriceBook(priceBook) {
    if (!priceBook?._id) return
    selectedPriceBooks.value = selectedPriceBooks.value.filter(b => b._id !== priceBook._id)
  }

  function handleSelectSegmentationBook(priceBook) {
    if (!priceBook?._id) return
    if (selectedPriceBookIds.value.includes(priceBook._id)) {
      deselectSegmentationBook(priceBook)
      return
    }
    selectedSegmentationBooks.value.push(priceBook)
  }

  function deselectSegmentationBook(priceBook) {
    if (!priceBook?._id) return
    selectedSegmentationBooks.value = selectedSegmentationBooks.value.filter(b => b._id !== priceBook._id)
  }

  function buildUpdatePayload() {
    const payload = {
      paymentTerms: localUserData.value.paymentTerms,
      logisticsTerms: localUserData.value.logisticsTerms,
      availableCurrencies: localUserData.value.availableCurrencies || [],
      value: discount.value > 100
        ? convertPercentToFraction(discount.value)
        : calculateCommercialTerms(discount.value),
    }

    const currencyChanged = userDataTransactionCurrency.value !== localUserData.value.transactionCurrency

    if (currencyChanged) {
      payload.priceBookIds = selectedSegmentationBooks.value.map(book => book._id)
    } else {
      payload.priceBookIds = selectedPriceBookIds.value
    }

    if (hasTransactionCurrencyFeature.value && localUserData.value.transactionCurrency) {
      payload.transactionCurrency = localUserData.value.transactionCurrency
    }

    return { payload, currencyChanged }
  }

  async function submitUpdate() {
    try {
      isEditing.value = true
      const { payload } = buildUpdatePayload()
      await store.dispatch(UPDATE_WALLET_RULE, {
        walletRuleId: localUserData.value.walletRuleId,
        payload,
      })
      apiToastSuccess('Updated successfully')
      return { success: true }
    } catch (err) {
      apiToastError(err)
      return { success: false }
    } finally {
      isEditing.value = false
    }
  }

  function updateDiscount() {
    discount.value = convertValueToPercent(localUserData.value.discount)
    if (localUserData.value.discount > 1) {
      discount.value += 100
    }
  }

  function addCurrency(currency) {
    if (!currency) return
    if (!localUserData.value.availableCurrencies) {
      localUserData.value.availableCurrencies = []
    }
    if (!localUserData.value.availableCurrencies.includes(currency)) {
      localUserData.value.availableCurrencies.push(currency)
    }
  }

  function removeCurrency(currency) {
    localUserData.value.availableCurrencies = (localUserData.value.availableCurrencies || []).filter(c => c !== currency)
  }

  return {
    localUserData,
    discount,
    isEditing,
    exampleWholeSalePrice,
    selectedPriceBooks,
    selectedSegmentationBooks,
    collectionWithPriceBooks,
    collectionWithSegmentationBooks,
    userDataTransactionCurrency,
    priceBooks,
    hasTransactionCurrencyFeature,
    getUpdatedPriceForRetailer,
    selectedPriceBookIds,
    regularPriceBooks,
    segmentationBooksData,
    isPriceBooksEmpty,
    isSegmentationBooksEmpty,
    discountError,
    paymentTermOptions,
    logisticsTermOptions,
    availableCurrencies,
    initEditUser,
    setPriceBooks,
    loadSelectedPriceBooks,
    loadSelectedSegmentationBooks,
    onSearchPriceBooks,
    onSearchSegmentationBooks,
    handleSelectPriceBook,
    deselectPriceBook,
    handleSelectSegmentationBook,
    deselectSegmentationBook,
    buildUpdatePayload,
    submitUpdate,
    updateDiscount,
    addCurrency,
    removeCurrency,
  }
}
